import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

const shareTargets = [
  { label: "微信", icon: "ri-wechat-line" },
  { label: "微博", icon: "ri-weibo-line" },
  { label: "朋友圈", icon: "ri-share-circle-line" },
];

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = newsList.find((news) => news.id === id);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareHint, setShareHint] = useState("");

  usePageMeta({
    title: item ? `${item.title} - 三级片资讯` : "资讯未找到 - 三级片资讯",
    description: item
      ? item.summary
      : "该资讯不存在或已下线，请返回资讯列表浏览其他三级片相关内容。",
    keywords: item ? `${item.category},三级片资讯,${item.title}` : "三级片资讯",
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="rise-in rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
          <i className="ri-file-unknow-line text-4xl text-background-400" />
          <h1 className="mt-4 text-xl font-bold text-foreground-950">资讯未找到</h1>
          <p className="mt-2 text-sm text-foreground-600">这篇资讯可能已被移除，或地址输入有误。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const sorted = [...newsList].sort((a, b) => b.date.localeCompare(a.date));
  const related = sorted.filter((news) => news.id !== item.id).slice(0, 3);

  const handleShare = (label: string) => {
    setShareHint(`已模拟分享到${label}（前端演示，未接入真实分享接口）`);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareHint("链接已复制（前端演示）");
    } catch {
      setShareHint("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <SiteLayout>
      <div className="rise-in">
        <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: item.category }]} />

        <article className="mt-4">
          <header>
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-accent-800">
                {item.category}
              </span>
              <span className="flex items-center gap-1 text-foreground-400">
                <i className="ri-calendar-line" />
                {item.date}
              </span>
              <span className="text-foreground-400">·</span>
              <span className="flex items-center gap-1 text-foreground-400">
                <i className="ri-user-line" />
                {item.author}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[34px]">
              {item.title}
            </h1>
            <p className="mt-3 border-l-2 border-secondary-500 pl-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              {item.summary}
            </p>
          </header>

          <div className="mt-6 h-56 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-[320px]">
            <img
              src={item.cover}
              alt={`${item.title} 资讯封面`}
              title={`${item.title} - 三级片影视资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">
            {item.blocks.map((block, index) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={`${item.id}-h2-${index}`}
                    className="mb-3 mt-8 flex items-center gap-2 border-l-2 border-primary-500 pl-2.5 font-heading text-lg font-bold text-foreground-950 md:text-xl"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote
                    key={`${item.id}-quote-${index}`}
                    className="my-6 rounded-lg bg-secondary-50 px-5 py-4 font-heading text-base italic leading-relaxed text-secondary-950"
                  >
                    <i className="ri-double-quotes-l mr-1 text-secondary-500" />
                    {block.text}
                  </blockquote>
                );
              }
              return (
                <p key={`${item.id}-p-${index}`} className="mb-4">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
            <Link
              to="/news"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-primary-700"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
            <button
              type="button"
              onClick={() => {
                setShareOpen(true);
                setShareHint("");
              }}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-share-forward-line" />
              分享这篇资讯
            </button>
          </div>
        </article>

        <section className="mt-12">
          <h2 className="mb-4 text-xl font-bold text-foreground-950 md:text-2xl">相关阅读</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {related.map((news) => (
              <Link
                key={news.id}
                to={`/news/${news.id}`}
                className="group cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="h-32 w-full overflow-hidden bg-background-200">
                  <img
                    src={news.cover}
                    alt={`${news.title} 资讯配图`}
                    title={`${news.title} - 三级片影视资讯`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-4">
                  <p className="text-[11px] text-foreground-400">{news.date}</p>
                  <h3 className="mt-1.5 line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                    {news.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {shareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="关闭分享弹窗"
            onClick={() => setShareOpen(false)}
            className="absolute inset-0 h-full w-full cursor-pointer bg-primary-950/55"
          />
          <div className="fade-in relative w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-base font-bold text-foreground-950">分享资讯</h2>
                <p className="mt-1 text-xs text-foreground-500">此功能为前端演示，不会真正发布内容</p>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              {shareTargets.map((target) => (
                <button
                  key={target.label}
                  type="button"
                  onClick={() => handleShare(target.label)}
                  className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={`${target.icon} text-xl`} />
                  {target.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={copyLink}
              className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-secondary-100 py-2.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              <i className="ri-link" />
              复制页面链接
            </button>

            {shareHint && (
              <p className="mt-3 rounded-md bg-background-100 px-3 py-2 text-center text-xs text-foreground-600">
                {shareHint}
              </p>
            )}
          </div>
        </div>
      )}
    </SiteLayout>
  );
}